import { FC, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Collapse } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CssIcon from '@mui/icons-material/Css';
import ImageIcon from '@mui/icons-material/Image';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { FileTreeNode } from '../../utils/fileUtils';

const INDENT_PX = 20;
const GUIDE_LEFT_OFFSET = 12;

const FILE_ICON_SX = { fontSize: 16, mr: 0.75 };

const FILE_ICON_MAP: Record<string, JSX.Element> = {
    json: <DataObjectIcon sx={{ ...FILE_ICON_SX, color: '#F9A825' }} />,
    css: <CssIcon sx={{ ...FILE_ICON_SX, color: '#42A5F5' }} />,
    svg: <ImageIcon sx={{ ...FILE_ICON_SX, color: '#66BB6A' }} />,
    png: <ImageIcon sx={{ ...FILE_ICON_SX, color: '#AB47BC' }} />,
    ico: <ImageIcon sx={{ ...FILE_ICON_SX, color: '#78909C' }} />,
};
const DEFAULT_FILE_ICON = <InsertDriveFileIcon sx={{ ...FILE_ICON_SX, color: 'action.active' }} />;

const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return FILE_ICON_MAP[ext] || DEFAULT_FILE_ICON;
};

const TreeGuide: FC<{ depth: number; variant: 'vertical' | 'horizontal' }> = ({ depth, variant }) => {
    const left = `${(depth - 1) * INDENT_PX + GUIDE_LEFT_OFFSET}px`;
    if (variant === 'vertical') {
        return (
            <Box sx={{
                position: 'absolute', left,
                top: 0, bottom: '50%', width: '1px',
                borderLeft: '1px solid', borderColor: 'divider',
            }} />
        );
    }
    return (
        <Box sx={{
            position: 'absolute', left,
            top: '50%', width: `${GUIDE_LEFT_OFFSET - 2}px`, height: '1px',
            borderBottom: '1px solid', borderColor: 'divider',
        }} />
    );
};

export type FileTreeItemCallbacks = {
    onAddFile: (folderPath: string) => void;
    onAddFolder: (parentPath: string) => void;
    onRemovePendingFile: (path: string) => void;
    onRemoveVirtualFolder: (path: string) => void;
};

export const FileTreeItem: FC<{
    node: FileTreeNode;
    depth: number;
} & FileTreeItemCallbacks> = ({ node, depth, onAddFile, onAddFolder, onRemovePendingFile, onRemoveVirtualFolder }) => {
    const [expanded, setExpanded] = useState(depth < 2);
    const [hovered, setHovered] = useState(false);

    const indent = depth * INDENT_PX;
    const hoverHandlers = {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
    };

    if (!node.isFolder) {
        return (
            <Box
                sx={{
                    display: 'flex', alignItems: 'center',
                    pl: `${indent}px`, pr: 1, py: '3px',
                    borderRadius: 0.5, position: 'relative',
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
                {...hoverHandlers}
            >
                {depth > 0 && <TreeGuide depth={depth} variant="vertical" />}
                {depth > 0 && <TreeGuide depth={depth} variant="horizontal" />}

                {getFileIcon(node.name)}
                <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.4 }}>
                    {node.name}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.disabled', fontFamily: 'monospace', fontSize: 10,
                        mr: 1, opacity: hovered ? 1 : 0, transition: 'opacity 0.15s',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {node.fullPath}
                </Typography>
                {node.isPending && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Tooltip title="Pending upload" arrow>
                            <ScheduleIcon sx={{ fontSize: 15, color: 'warning.main' }} />
                        </Tooltip>
                        <Tooltip title="Remove" arrow>
                            <IconButton
                                size="small"
                                sx={{ p: 0.25, opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}
                                onClick={() => onRemovePendingFile(node.fullPath)}
                            >
                                <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        );
    }

    const callbacks = { onAddFile, onAddFolder, onRemovePendingFile, onRemoveVirtualFolder };

    return (
        <Box sx={{ position: 'relative' }}>
            {expanded && node.children.length > 0 && (
                <Box sx={{
                    position: 'absolute',
                    left: `${depth * INDENT_PX + GUIDE_LEFT_OFFSET}px`,
                    top: 26, bottom: 4, width: '1px',
                    borderLeft: '1px solid', borderColor: 'divider',
                }} />
            )}
            {depth > 0 && <TreeGuide depth={depth} variant="vertical" />}
            {depth > 0 && <TreeGuide depth={depth} variant="horizontal" />}
            <Box
                sx={{
                    display: 'flex', alignItems: 'center',
                    pl: `${indent}px`, pr: 1, py: '3px',
                    cursor: 'pointer', borderRadius: 0.5,
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
                onClick={() => setExpanded(prev => !prev)}
                {...hoverHandlers}
            >
                {expanded ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                {expanded
                    ? <FolderOpenIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.75 }} />
                    : <FolderIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.75 }} />
                }
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.4 }}>
                    {node.name}
                </Typography>
                <Box sx={{ display: 'flex', opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
                    {node.isVirtual && (
                        <Tooltip title="Remove folder" arrow>
                            <IconButton size="small" sx={{ p: 0.25 }}
                                onClick={(e) => { e.stopPropagation(); onRemoveVirtualFolder(node.fullPath); }}
                            >
                                <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Add file here" arrow>
                        <IconButton size="small" sx={{ p: 0.25 }}
                            onClick={(e) => { e.stopPropagation(); onAddFile(node.fullPath); }}
                        >
                            <NoteAddIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="New subfolder" arrow>
                        <IconButton size="small" sx={{ p: 0.25 }}
                            onClick={(e) => { e.stopPropagation(); onAddFolder(node.fullPath); }}
                        >
                            <CreateNewFolderIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Collapse in={expanded}>
                {node.children.map(child => (
                    <FileTreeItem key={child.fullPath} node={child} depth={depth + 1} {...callbacks} />
                ))}
                {node.children.length === 0 && (
                    <Typography
                        variant="caption" color="text.disabled"
                        sx={{ pl: `${(depth + 1) * INDENT_PX + 8}px`, display: 'block', py: '2px', fontStyle: 'italic', fontSize: 11 }}
                    >
                        Empty folder
                    </Typography>
                )}
            </Collapse>
        </Box>
    );
};
